import type { OnAirScheduleBlockResolved } from "../components/onair/OnAirSchedule.component";
import type { AssetLegacyProps } from "../content-blocks/AssetLegacy.component";
import { CatalogProps } from "../content-blocks/Catalog.component";
import type { CatalogLegacyProps } from "../content-blocks/CatalogLegacy.component";
import type { ContestEmbedLegacyLoaderProps } from "../content-blocks/ContestEmbedLegacyLoader.component";
import type { PayloadInterface, PayloadInterfaceLegacy } from "../decoders/Content.types";
import { BlockBase } from "../decoders/ContentBlocks.types";
import type { ErrorResponse, Recipient } from "../decoders/PostUp.types";
import type {
    GalleryInterface,
    GalleryLegacyInterface,
    IHeartContentInterface,
    IHeartContentLegacyInterface,
} from "../decoders/PubSub.types";
import type { ResolvedBlockData } from "../decoders/Sites.types";
import type { BlockFragment, SitesFeedPwsResult } from "../generated/webapi.g";
import * as Webapi from "../generated/webapi.g";
import type { ResolvedTitle } from "../page-blocks/datasource/DatasourceLoader.component";
import type { ResolvedChartListBlock } from "../services/Charts";

import type { AssetProps } from "./asset";
import type { EmbedLegacyProps, HtmlLegacyProps, HtmlProps } from "./embed";
import type { PlainTextProps } from "./plaintext";

export function isChartListBlock(v?: any): v is ResolvedChartListBlock {
    return !!(v && v.hasOwnProperty("chartTitle") && v.hasOwnProperty("list") && isChartList(v.list));
}

export function isChartList(v?: any): v is Webapi.ChartsListFragment {
    return !!(
        v &&
        v.hasOwnProperty("topItems") &&
        v.hasOwnProperty("slug") &&
        v.hasOwnProperty("image") &&
        v.hasOwnProperty("title") &&
        v.hasOwnProperty("songs")
    );
}

export function isChart(v?: any): v is Webapi.ChartV2Fragment {
    return !!(
        v &&
        v.hasOwnProperty("slug") &&
        v.hasOwnProperty("id") &&
        v.hasOwnProperty("title") &&
        v.hasOwnProperty("subtitle")
    );
}

export function isAptivadaAppFragment(v?: ResolvedBlockData): v is Webapi.AptivadaAppFragment {
    return !!(
        v &&
        v.hasOwnProperty("appType") &&
        v.hasOwnProperty("appId") &&
        v.hasOwnProperty("parentAppId") &&
        v.hasOwnProperty("title") &&
        v.hasOwnProperty("shareTitle") &&
        v.hasOwnProperty("tags") &&
        v.hasOwnProperty("gridStatus") &&
        v.hasOwnProperty("gridRedirect") &&
        v.hasOwnProperty("pageUrl")
    );
}

export function isFeedResult(
    v?:
        | Webapi.FeedResult_SitesFeedLeadsResult_Fragment
        | Webapi.FeedResult_SitesFeedPubsubResult_Fragment
        | Webapi.FeedResult_SitesFeedPwsResult_Fragment
        | Pick<SitesFeedPwsResult, "id" | "type">
        | null
        | undefined,
): v is
    | Webapi.FeedResult_SitesFeedLeadsResult_Fragment
    | Webapi.FeedResult_SitesFeedPubsubResult_Fragment
    | Webapi.FeedResult_SitesFeedPwsResult_Fragment {
    return !!(v && v.hasOwnProperty("id") && v.hasOwnProperty("type") && v.hasOwnProperty("record"));
}

export function isPublishRecord(
    v?: Webapi.FeedResultFragment["record"] | undefined,
): v is Webapi.PublishRecordFragment {
    return !!(
        v &&
        v.hasOwnProperty("payload") &&
        v.hasOwnProperty("pub_start") &&
        v.hasOwnProperty("slug") &&
        v.hasOwnProperty("subscription") &&
        v.hasOwnProperty("summary")
    );
}

export function isCalendarPublishRecord(
    v?: Webapi.FeedResultFragment["record"] | undefined,
): v is Webapi.PublishRecordFragment {
    return !!(v && isPublishRecord(v) && v.hasOwnProperty("type") && v.type === "content:calendar");
}

function isPWSGuestRecordSummary(v: any): v is Webapi.PwsGuestRecordFragment {
    return (
        !!v.summary &&
        v.summary.hasOwnProperty("title") &&
        v.summary.hasOwnProperty("description") &&
        v.summary.hasOwnProperty("image")
    );
}

export function isPWSGuestRecord(
    v?: Webapi.FeedResultFragment["record"] | undefined,
): v is Webapi.PwsGuestRecordFragment {
    return !!(
        v &&
        v.hasOwnProperty("id") &&
        v.hasOwnProperty("slug") &&
        v.hasOwnProperty("summary") &&
        isPWSGuestRecordSummary(v)
    );
}

export function isLeadsCard(v?: Webapi.FeedResultFragment["record"] | undefined): v is Webapi.LeadsCardFragment {
    return !!(
        v &&
        v.hasOwnProperty("catalog") &&
        v.hasOwnProperty("link") &&
        v.hasOwnProperty("use_catalog_image") &&
        v.hasOwnProperty("subtitle") &&
        v.hasOwnProperty("title")
    );
}

export function isResolvedTitle(v: any): v is ResolvedTitle {
    return !!v && v.hasOwnProperty("value") && v.hasOwnProperty("topicTitle");
}

export function isItemBlock(block?: BlockFragment): block is Webapi.Block_SitesItemBlock_Fragment {
    return block && ["item-reference", "item"].includes(block.type) && (block as any).hasOwnProperty("item");
}

export function isFeedBlock(block?: BlockFragment): block is Webapi.Block_SitesFeedBlock_Fragment {
    return block && block.type === "feed" && (block as any).hasOwnProperty("feed");
}

export function isCalendarFeedBlock(block?: BlockFragment): block is Webapi.Block_SitesFeedBlock_Fragment {
    return !!(block && isFeedBlock(block) && block?.value.feed_id === "ID:calendar-topics-feed-matsubs");
}

// Block_SitesKeywordContestBlock_Fragment
// KeywordContestFragment
export function isKeywordContestBlock(block?: BlockFragment): block is Webapi.Block_SitesKeywordContestBlock_Fragment {
    return block && block.type === "keywordcontest" && (block as any).hasOwnProperty("schedule");
}

export interface ResolvedPubsubPublishRecord extends Webapi.PubsubPublishRecord {
    eyebrow?: Webapi.Eyebrow;
}

export interface OnAirScheduleBlock {
    resolved: OnAirScheduleBlockResolved;
}

export function isOnAirScheduleBlock(block: any): block is OnAirScheduleBlock {
    return !!(
        block &&
        block.resolved &&
        block.resolved.schedule &&
        block.resolved.schedule.names &&
        block.resolved.schedule.names.length
    );
}

export function isWebapiCalendarEventArray(v: any): v is Webapi.CalendarEvent[] {
    return !!(v && v.length && v.filter(isWebapiCalendarEvent));
}

export function isWebapiCalendarEvent(v: any): v is Webapi.CalendarEvent {
    return !!(
        v &&
        v.hasOwnProperty("content") &&
        v.content.hasOwnProperty("record") &&
        v.content.record &&
        v.content.record.hasOwnProperty("payload") &&
        v.content.record.payload &&
        v.content.record.payload.hasOwnProperty("blocks") &&
        v.content.record.payload &&
        v.content.record.payload.hasOwnProperty("canonical_url") &&
        v.hasOwnProperty("tags") &&
        v.hasOwnProperty("eventId")
    );
}

export function isRecipientResponse(response: any): response is Recipient {
    return !!(response && response.recipientId && response.status);
}

export function isErroneousResponse(response: any): response is ErrorResponse {
    return response && response.data && response.status === "error";
}

export function isSiteSection(v: any): v is Webapi.UserConfigFragment {
    return !!(
        v &&
        (v.hasOwnProperty("general") ||
            v.hasOwnProperty("alert") ||
            v.hasOwnProperty("navigation") ||
            v.hasOwnProperty("design") ||
            v.hasOwnProperty("social") ||
            v.hasOwnProperty("contact") ||
            v.hasOwnProperty("partners") ||
            v.hasOwnProperty("ads") ||
            v.hasOwnProperty("analytics"))
    );
}

export function isSitesFeedPubsubResult(v: any): v is Webapi.FeedResult_SitesFeedPubsubResult_Fragment {
    return (
        v &&
        v.hasOwnProperty("type") &&
        v.type === Webapi.SitesQueryDatasource.Publishing &&
        v.hasOwnProperty("record") &&
        v.record &&
        v.record.hasOwnProperty("payload") &&
        v.record.hasOwnProperty("pub_start") &&
        v.record.hasOwnProperty("slug") &&
        v.record.hasOwnProperty("subscription") &&
        v.record.hasOwnProperty("summary")
    );
}

export function isSitesFeedPwsResult(v: any): v is Webapi.FeedResult_SitesFeedPwsResult_Fragment {
    return (
        v &&
        v.hasOwnProperty("type") &&
        v.type === Webapi.SitesQueryDatasource.Pws &&
        v.hasOwnProperty("record") &&
        v.record &&
        v.record.hasOwnProperty("id") &&
        v.record.hasOwnProperty("slug")
    );
}

export function isCurrentlyPlayingTrackDetailFragment(v: any): v is Webapi.CurrentlyPlayingTrackDetailFragment {
    return (
        v &&
        v.hasOwnProperty("dataSource") &&
        v.hasOwnProperty("startTime") &&
        v.hasOwnProperty("endTime") &&
        v.hasOwnProperty("status") &&
        v.hasOwnProperty("offset") &&
        v.hasOwnProperty("trackDurationInSec")
    );
}

export function isTopTrackDetailFragmentList(v: any[]): v is Webapi.TopTrackDetailFragment[] {
    return v.map(track => !isCurrentlyPlayingTrackDetailFragment(track)).length === v.length;
}

export function isLegacyHtmlTextBlock(v: any): v is HtmlLegacyProps {
    // block type : text/html
    return v && v.hasOwnProperty("type") && (v.type === "html" || v.type === "text") && v.hasOwnProperty("html");
}

export function isHtmlTextBlock(v: any): v is HtmlProps {
    // block type : text/html
    return v && v.hasOwnProperty("type") && (v.type === "html" || v.type === "text") && v.hasOwnProperty("value");
}

export function isPlainBlock(v: any): v is PlainTextProps {
    return v && v.hasOwnProperty("type") && v.type === "plain" && v.hasOwnProperty("value");
}

export function isPrimaryAssetBlock(v: any): any {
    return v && v.hasOwnProperty("type") && v.type === "asset" && v.hasOwnProperty("primaryAsset");
}

export function isLegacyAssetBlock(v: any): v is AssetLegacyProps {
    // block type : asset
    return (
        v &&
        v.hasOwnProperty("type") &&
        v.type === "asset" &&
        v.hasOwnProperty("bucket") &&
        v.hasOwnProperty("id") &&
        v.hasOwnProperty("new_tab") &&
        v.hasOwnProperty("title") &&
        v.hasOwnProperty("url")
    );
}

export function isAssetBlock(v: any): v is AssetProps {
    return v && v.hasOwnProperty("type") && v.type === "asset" && v.hasOwnProperty("value");
}

export function isLegacyIHeartContentBlock(v: any): v is IHeartContentLegacyInterface {
    // block type : iHeartContent
    return (
        v &&
        v.hasOwnProperty("type") &&
        v.type === "iheartcontent" &&
        v.hasOwnProperty("id") &&
        v.hasOwnProperty("summary") &&
        v.hasOwnProperty("schedule") &&
        v.hasOwnProperty("canonical_url")
    );
}

export function isIHeartContentBlock(v: any): v is IHeartContentInterface {
    // block type : iHeartContent
    return (
        v &&
        v.hasOwnProperty("type") &&
        v.type === "content" &&
        v.hasOwnProperty("value") &&
        v.value.hasOwnProperty("id") &&
        v.value.hasOwnProperty("canonical_url") &&
        v.value.hasOwnProperty("image")
    );
}

export function isLegacyGalleryBlock(v: any): v is GalleryLegacyInterface {
    // block type : gallery
    return (
        v &&
        v.hasOwnProperty("type") &&
        v.type === "gallery" &&
        v.hasOwnProperty("schedule") &&
        v.hasOwnProperty("assets")
    );
}

export function isGalleryBlock(v: any): v is GalleryInterface {
    // block type : gallery
    return (
        v &&
        v.hasOwnProperty("type") &&
        v.type === "gallery" &&
        v.hasOwnProperty("schedule") &&
        v.hasOwnProperty("value")
    );
}

export function isLegacyCatalogBlock(v: any): v is CatalogLegacyProps {
    // block type : catalog
    return v && v.hasOwnProperty("type") && v.type === "catalog" && v.hasOwnProperty("kind");
}

export function isCatalogBlock(v: any): v is CatalogProps {
    // block type : catalog
    return (
        v &&
        v.hasOwnProperty("type") &&
        v.type === "catalog" &&
        v.hasOwnProperty("value") &&
        v.value.hasOwnProperty("kind")
    );
}

export function isContestBlock(v: any): v is ContestEmbedLegacyLoaderProps {
    // block type : contest
    return (
        v &&
        v.hasOwnProperty("type") &&
        v.type === "contest" &&
        v.hasOwnProperty("call_letters") &&
        v.hasOwnProperty("contest_id") &&
        v.hasOwnProperty("url")
    );
}

export function isEmbedLegacyBlock(v: any): v is EmbedLegacyProps {
    // block type : embed
    return (
        v &&
        v.hasOwnProperty("type") &&
        v.type === "embed" &&
        v.hasOwnProperty("embed_type") &&
        v.hasOwnProperty("html")
    );
}

export function isEmbedBlock<T extends { value?: any; provider?: string }>(v: any): v is T {
    // block type : embed
    // embed types: video,rich
    // provider: facebook,twitter,instagram,iheratradio,imgur,yotuube,soundcloud,crowdsignal,streamable,ohembed
    return (
        v &&
        v.hasOwnProperty("type") &&
        v.type === "embed" &&
        v.hasOwnProperty("value") &&
        v.value.hasOwnProperty("embed_type") &&
        v.value.hasOwnProperty("html")
    );
}

export const isPayloadInterface = (
    v: PayloadInterfaceLegacy | Webapi.ContentPayload | PayloadInterface,
): v is PayloadInterface => v.hasOwnProperty("fields");

export function isYouTubePlaylist(block: BlockFragment): block is Webapi.Block_SitesYoutubeBlock_Fragment {
    return block && block.type === "youtube" && block.hasOwnProperty("playlist");
}

export function isYouTubeChannel(block: BlockFragment): block is Webapi.Block_SitesYoutubeBlock_Fragment {
    return block && block.type === "youtube" && block.hasOwnProperty("channel");
}

export function isBlockScheduled({ schedule }: BlockBase, now = Date.now()) {
    if (!schedule || !schedule.enabled) {
        return true;
    }

    const begin = schedule.begin || now;
    const end = schedule.end || Number.MAX_SAFE_INTEGER;

    return begin <= now && end > now;
}
